// ViewFiscalTicketModal.jsx
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, CircularProgress, Typography } from '@mui/material';
import { fetchFiscalTicket, fetchInvoiceTickets } from '../../services/SalesService';
import { base64ToBlob } from './../../utils/ImageUtils'

const ViewFiscalTicketModal = ({
  open,
  onClose,
  invoiceId,
  source = 'treat',
  fallbackExternalDocumentId = null,
  fallbackRut = null,
  useAccountingFallback = false,
  preferCreditNote = false,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [invoicePdfUrl, setInvoicePdfUrl] = useState(null);
  const [creditNotePdfUrl, setCreditNotePdfUrl] = useState(null);
  const [deliveryPdfUrl, setDeliveryPdfUrl] = useState(null);
  const [pendingCreditNote, setPendingCreditNote] = useState(false);
  const [pendingElectronicInvoice, setPendingElectronicInvoice] = useState(false);

  useEffect(() => {
    const fetchTicket = async () => {
      if (!invoiceId) return;
      setLoading(true);
      setInvoicePdfUrl(null);
      setCreditNotePdfUrl(null);
      setDeliveryPdfUrl(null);
      setPendingCreditNote(false);
      setPendingElectronicInvoice(false);
      try {
        if (source === 'provider') {
          const pdfBlob = await fetchFiscalTicket(invoiceId, fallbackRut ?? undefined, {
            skipGlobalErrorRedirect: useAccountingFallback,
          });
          if (pdfBlob) {
            const url = URL.createObjectURL(pdfBlob);
            setInvoicePdfUrl(url);
          }
        } else {
          const tickets = await fetchInvoiceTickets(invoiceId, {
            skipGlobalErrorRedirect: useAccountingFallback,
          });
          const payload = tickets?.data?.data || tickets?.data || tickets || {};
          if (payload?.factura) {
            const invoiceBlob = base64ToBlob(payload.factura);
            const invoiceUrl = URL.createObjectURL(invoiceBlob);
            setInvoicePdfUrl(invoiceUrl);
          }
          if (payload?.creditNoteFactura) {
            const creditNoteBlob = base64ToBlob(payload.creditNoteFactura);
            const creditNoteUrl = URL.createObjectURL(creditNoteBlob);
            setCreditNotePdfUrl(creditNoteUrl);
          }
          setPendingCreditNote(payload?.pendingCreditNote === true);
          setPendingElectronicInvoice(payload?.pendingElectronicInvoice === true);
          if (payload?.deliveryTicket) {
            const deliveryBlob = base64ToBlob(payload.deliveryTicket);
            const deliveryUrl = URL.createObjectURL(deliveryBlob);
            setDeliveryPdfUrl(deliveryUrl);
          }

          if (!payload?.factura && useAccountingFallback && fallbackExternalDocumentId) {
            const fallbackBlob = await fetchFiscalTicket(fallbackExternalDocumentId, fallbackRut ?? undefined, {
              skipGlobalErrorRedirect: true,
            });
            if (fallbackBlob) {
              const fallbackUrl = URL.createObjectURL(fallbackBlob);
              setInvoicePdfUrl(fallbackUrl);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching ticket PDF:', error);
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      fetchTicket();
    }
  }, [open, invoiceId, source, fallbackExternalDocumentId, fallbackRut, useAccountingFallback]);

  useEffect(() => {
    return () => {
      if (invoicePdfUrl) URL.revokeObjectURL(invoicePdfUrl);
      if (creditNotePdfUrl) URL.revokeObjectURL(creditNotePdfUrl);
      if (deliveryPdfUrl) URL.revokeObjectURL(deliveryPdfUrl);
    };
  }, [invoicePdfUrl, creditNotePdfUrl, deliveryPdfUrl]);

  const invoiceEntry = invoicePdfUrl
    ? { key: 'invoice', title: 'Ticket original', url: invoicePdfUrl, iframeTitle: 'Ticket PDF' }
    : null;
  const creditNoteEntry = creditNotePdfUrl
    ? { key: 'credit-note', title: 'Nota de crédito', url: creditNotePdfUrl, iframeTitle: 'Nota de credito PDF' }
    : null;
  const deliveryEntry = deliveryPdfUrl
    ? { key: 'delivery', title: 'Ticket de envio', url: deliveryPdfUrl, iframeTitle: 'Ticket envio PDF' }
    : null;
  const pdfEntries = preferCreditNote
    ? [creditNoteEntry || (pendingCreditNote ? null : invoiceEntry), deliveryEntry].filter(Boolean)
    : [invoiceEntry, deliveryEntry].filter(Boolean);
  const hasAnyPdf = pdfEntries.length > 0;
  const hasMultiplePdfs = pdfEntries.length > 1;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          display: 'flex',
          flexDirection: 'column',
        }
      }}
    >
      <DialogTitle>Visualizar Ticket</DialogTitle>
      {pendingElectronicInvoice && !loading && (
        <Alert severity="info" sx={{ mx: 2, mb: 1 }}>
          {t('electronic_invoice_pending_notice')}
        </Alert>
      )}
      <DialogContent
        dividers
        sx={{
          p: 0,
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <CircularProgress />
          </Box>
        ) : hasAnyPdf ? (
          <Box
            sx={{
              height: '100%',
              minHeight: 0,
              overflowY: 'auto',
              p: 2,
              display: 'grid',
              gap: 2,
              gridAutoRows: hasMultiplePdfs ? 'minmax(420px, 1fr)' : '1fr',
            }}
          >
            {pdfEntries.map((pdf) => (
              <Box
                key={pdf.key}
                sx={{
                  minHeight: hasMultiplePdfs ? 420 : 0,
                  height: hasMultiplePdfs ? 'auto' : '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1 }}>{pdf.title}</Typography>
                <Box sx={{ flex: 1, minHeight: hasMultiplePdfs ? 420 : 0 }}>
                  <iframe
                    src={pdf.url}
                    title={pdf.iframeTitle}
                    width="100%"
                    height="100%"
                    style={{ border: 'none' }}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <p>{preferCreditNote ? 'No se pudo cargar la nota de crédito.' : 'Error cargando el ticket.'}</p>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewFiscalTicketModal;
